
import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { LANGUAGES } from "./constants";
import { GoogleGenAI } from "@google/genai";

// We mock the SDK structure so we don't have to refactor the whole app.
// Instead of calling Gemini directly, this calls your "generateAI" Cloud Function.
const generateAI = httpsCallable(functions, 'generateAI');

export const SYSTEM_INSTRUCTION_TRANSLATOR = `
You are a helpful translation bridge. 
Translate the input text to the target language.
Maintain a warm, professional tone.
If the text is already in the target language, return it as is.
`;

let activeOrgApiKey: string | undefined = undefined;

export const setActiveOrgApiKey = (key?: string) => {
  activeOrgApiKey = key?.trim() || undefined;
};

export const getActiveOrgApiKey = () => activeOrgApiKey;

export const ai = {
  models: {
    generateContent: async (params: any) => {
      // 1. If institution configured their own BYOK Gemini API key, use direct client
      if (activeOrgApiKey) {
        try {
          const directAi = new GoogleGenAI({ apiKey: activeOrgApiKey });
          const response = await directAi.models.generateContent(params);
          return {
            text: response.text,
            candidates: response.candidates || [
              {
                content: {
                  parts: [{ text: response.text }]
                }
              }
            ]
          };
        } catch (directErr: any) {
          console.warn("BYOK Custom API Key call failed, trying fallback:", directErr);
          // Fallback to backend cloud function if direct call failed
        }
      }

      try {
        // Pass the exact parameters expected by the SDK to the Cloud Function
        const result: any = await generateAI(params);
        
        return {
          text: result.data.text,
          candidates: result.data.candidates || [
            {
              content: {
                parts: [{ text: result.data.text }]
              }
            }
          ]
        };
      } catch (error: any) {
        console.error("AI Service Error:", error);
        
        let msg = error.message || "Failed to generate content via secure backend.";
        
        // Improve error message if it's the known "API key not valid" issue
        if (msg.includes("API key not valid") || msg.includes("API_KEY_INVALID")) {
            msg = "Configuration Error: The Server API Key is invalid. Please update the GEMINI_API_KEY secret in Firebase or configure a custom API key in School Settings.";
        }

        throw new Error(msg);
      }
    }
  }
};

// Helper function to maintain existing retry logic pattern
export const generateContentWithRetry = async (
    params: any,
    retries = 3,
    delay = 1000
) => {
    for (let i = 0; i < retries; i++) {
        try {
            return await ai.models.generateContent(params);
        } catch (error: any) {
            console.warn(`AI Attempt ${i + 1} failed. Retrying...`, error);
            
            // Don't retry if it's a configuration error (API Key)
            if (error.message && error.message.includes("Configuration Error")) {
                throw error;
            }

            if (i < retries - 1) {
                await new Promise(res => setTimeout(res, delay * Math.pow(2, i)));
                continue;
            }
            throw error;
        }
    }
    throw new Error("Max retries reached");
};

export const translateContent = async (text: string, targetLanguageCode: string): Promise<string> => {
    if (!text) return "";
    const targetLangLabel = LANGUAGES.find(l => l.code === targetLanguageCode)?.label || targetLanguageCode;
    try {
        const response = await generateContentWithRetry({
            model: 'gemini-3-flash-preview',
            contents: `Translate the following text to ${targetLangLabel}. Maintain the original tone and formatting. If it is already in the target language, return it as is.\n\nText: "${text}"`,
            config: { systemInstruction: SYSTEM_INSTRUCTION_TRANSLATOR }
        });
        return response.text || text;
    } catch (error) {
        console.error("Translation failed:", error);
        return text;
    }
};

export const generateSpeech = async (text: string, languageCode: string = 'en'): Promise<string | null> => {
    if (!text) return null;
    try {
        // Use the cloud function (via generateContentWithRetry) which now supports returning candidates
        const response = await generateContentWithRetry({
            model: 'gemini-3.1-flash-tts-preview',
            contents: [{
                parts: [{ text: text }]
            }],
            config: {
                responseModalities: ['AUDIO'],
                speechConfig: {
                    voiceConfig: {
                        prebuiltVoiceConfig: {
                            voiceName: 'Puck' 
                        }
                    }
                }
            }
        });

        // Extract base64 audio
        const candidate = response.candidates?.[0];
        const part = candidate?.content?.parts?.[0];
        
        if (part?.inlineData?.data) {
            const pcmBase64 = part.inlineData.data;
            const binaryStr = window.atob(pcmBase64);
            const len = binaryStr.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binaryStr.charCodeAt(i);
            }
            const wavHeader = new Uint8Array(44);
            const view = new DataView(wavHeader.buffer);
            view.setUint32(0, 0x52494646, false); // "RIFF"
            view.setUint32(4, 36 + len, true);
            view.setUint32(8, 0x57415645, false); // "WAVE"
            view.setUint32(12, 0x666D7420, false); // "fmt "
            view.setUint32(16, 16, true);
            view.setUint16(20, 1, true); // PCM
            view.setUint16(22, 1, true); // 1 channel
            view.setUint32(24, 24000, true); // sample rate
            view.setUint32(28, 24000 * 2, true); // byte rate
            view.setUint16(32, 2, true); // block align
            view.setUint16(34, 16, true); // 16-bit
            view.setUint32(36, 0x64617461, false); // "data"
            view.setUint32(40, len, true);

            const wavBytes = new Uint8Array(44 + len);
            wavBytes.set(wavHeader, 0);
            wavBytes.set(bytes, 44);

            let binary = '';
            const chunk = 8192;
            for (let i = 0; i < wavBytes.length; i += chunk) {
                binary += String.fromCharCode.apply(null, Array.from(wavBytes.subarray(i, i + chunk)));
            }
            return `data:audio/wav;base64,${window.btoa(binary)}`;
        }
        return null;

    } catch (error) {
        console.error("Speech generation failed:", error);
        return null;
    }
};